import {
  PREDICATE_PROPERTY_OPERATOR,
  SUBTRACTIVE_OPERATORS
} from "constants/filter.constants";
import { UNIT_IDS } from "types/units";

import { IPredicate, PropertyFilterModel } from "models/filter";
import {
  SHAPEFILE_BOUNDARY_COLUMN,
  SHAPEFILE_MATCH_MODE,
  ShapefileMatchMode,
  buildShapefileProperty,
  isContainedWithin,
  withShapefileMatchMode
} from "models/groupBy";
import { PdenSourceEnum } from "models/pdenDataSourceSetting";

/**
 * What the shapefile filtering toggle can be set to. "off" is the absence of a
 * predicate rather than a mode, so it never reaches the wire — the two real
 * modes are the ones a predicate can carry.
 */
export const SHAPEFILE_FILTER_OFF = "off";
export type ShapefileFilterMode = typeof SHAPEFILE_FILTER_OFF | ShapefileMatchMode;

/** Separates the shapefile's name from the mode wherever the filter is shown. */
const MODE_SEPARATOR = " · ";

/**
 * Metres in a mile. The buffer is stored in metres and said in whichever unit
 * the person reading it works in. Exported so the control that types a distance
 * in miles and the label that reads it back agree to the digit.
 */
export const METRES_PER_MILE = 1609.34;

/** Metres in a kilometre, the metric half of the same job. */
export const METRES_PER_KILOMETRE = 1000;

/**
 * The furthest a buffer can reach, a thousand miles — the distance the backend
 * clamps to. Kept in one place so the control, the label and the ring all say
 * the same thing about where the far end is. What the backend accepts is
 * written down once, in this folder's CONTEXT.md.
 */
export const MAX_SHAPEFILE_BUFFER_METRES = 1000 * METRES_PER_MILE;

/**
 * The step every distance the backend filters at lands on, mirroring its
 * BUFFER_QUANTUM_METERS: it snaps the distance it resolves to the nearest ten
 * metres before that distance reaches a cache key or the geometry. The cap is
 * a whole number of these, so the furthest a buffer reaches is itself a
 * distance the filter can run at.
 */
export const SHAPEFILE_BUFFER_QUANTUM_METRES = 10;

/**
 * A buffer as it is actually filtered at: whole metres, no further than the
 * cap, on the same ten-metre step the backend measures at. Rounded, capped and
 * snapped everywhere a predicate is read or written rather than only where a
 * distance is typed, so a value from an older build, another client or a
 * hand-edited saved filter is healed on its way through — the backend takes
 * whatever shape of number it is sent, rounding a fraction and saturating one
 * past its range, so what is at stake is not the request but whether the chip,
 * the box and the filter all name one distance.
 *
 * The step is what keeps a stored buffer honest. Without it a filter could
 * hold 1609 m while the backend measured 1610, so the chip would name a
 * distance nothing ran at; and a stored distance under half a step would be
 * labelled as a buffer while the backend filtered unbuffered. Snapping here
 * means a stored buffer is always the distance the filter ran at, and one too
 * small to reach a step heals to none — dropping the field exactly as a zero
 * does, since no buffer is what the backend would have applied anyway.
 */
function usableBufferMetres(metres: Maybe<number>): number {
  if (!(metres > 0)) {
    return 0;
  }
  const capped = Math.min(Math.round(metres), MAX_SHAPEFILE_BUFFER_METRES);
  return (
    Math.round(capped / SHAPEFILE_BUFFER_QUANTUM_METRES) * SHAPEFILE_BUFFER_QUANTUM_METRES
  );
}

/**
 * The larger unit of each pair the buffer control offers, one pair per system
 * of measurement: whoever reads lengths in feet gets miles, everybody else
 * kilometres. It is where the switch starts, and what a label falls back to
 * when nothing usable has been picked on it.
 */
export type ShapefileBufferLargerUnit =
  | typeof UNIT_IDS.MILES
  | typeof UNIT_IDS.KILOMETERS;

/**
 * The unit a buffer is spelled in: any of the four the buffer control can be
 * switched to, so a distance is read back in the unit it was asked for rather
 * than in whichever unit is the larger of the pair. Whoever typed 500 m reads
 * "(+500 m)" and not "(+0.5 km)".
 */
export type ShapefileBufferLabelUnit =
  | ShapefileBufferLargerUnit
  | typeof UNIT_IDS.METERS
  | typeof UNIT_IDS.FEET;

/**
 * Metres per unit a buffer can be typed in or read back in. Exported so the
 * control that types a distance and the label that spells it out convert the
 * one stored number the same way, to the digit.
 */
export const METRES_PER_LABEL_UNIT: Record<ShapefileBufferLabelUnit, number> = {
  [UNIT_IDS.MILES]: METRES_PER_MILE,
  [UNIT_IDS.KILOMETERS]: METRES_PER_KILOMETRE,
  [UNIT_IDS.FEET]: 0.3048,
  [UNIT_IDS.METERS]: 1
};

/**
 * A buffer as it was asked for: the number that was committed and the unit it
 * was committed in. It rides the predicate beside the metres so that the box
 * and the chip can say the distance back the way it was said, rather than the
 * round trip through the ten-metre step the filter runs on — a thousand feet
 * asked for reads a thousand feet rather than 984.25.
 *
 * It never decides anything. The filter runs on the metres, here and at the
 * backend, and this pair only chooses the wording when the unit it was said in
 * is the unit being read in.
 */
export interface ShapefileBufferDisplay {
  value: number;
  unit: ShapefileBufferLabelUnit;
}

/**
 * The display pair, when it is one that can be believed: a positive, finite
 * number in a unit a buffer can be said in, that comes back to the distance
 * the filter runs at within one step of it. Anything else — a pair from a
 * hand-edited saved filter, one left behind by a buffer that was changed
 * without it, a unit no build offers — is answered as nothing, and whoever
 * asked converts from the metres instead.
 *
 * One step of tolerance is what the round trip costs: no whole number of feet
 * or miles lands on a ten-metre step, so a thousand feet is stored as the 300 m
 * the filter measures at and comes back 4.8 m short of what was typed. Wider
 * than a step would let a pair name a distance the filter is not running at.
 */
function trustedBufferDisplay(
  value: Maybe<number>,
  unit: Maybe<string>,
  metres: number
): Maybe<ShapefileBufferDisplay> {
  if (typeof value !== "number" || !isFinite(value) || value <= 0) {
    return null;
  }
  if (
    typeof unit !== "string" ||
    !Object.prototype.hasOwnProperty.call(METRES_PER_LABEL_UNIT, unit)
  ) {
    return null;
  }
  const labelUnit = unit as ShapefileBufferLabelUnit;
  const said = value * METRES_PER_LABEL_UNIT[labelUnit];
  return Math.abs(said - metres) <= SHAPEFILE_BUFFER_QUANTUM_METRES
    ? { value, unit: labelUnit }
    : null;
}

/**
 * Which pair a reader's units come from, named by the larger of the two, from
 * the length unit the rest of the screen is in. Only feet mean imperial; the
 * converter answers metres for everyone until its engine has loaded, so a
 * reader nothing is known about yet gets kilometres — the same default the
 * buffer control starts on.
 */
export function shapefileBufferLabelUnit(
  displayUnit: Maybe<string>
): ShapefileBufferLargerUnit {
  return displayUnit === UNIT_IDS.FEET ? UNIT_IDS.MILES : UNIT_IDS.KILOMETERS;
}

/**
 * A distance under this shows as "+0", which reads as no buffer at all. It is
 * counted in whichever unit the label is being spelled in, so each unit has
 * its own floor. Nothing healed lands on one while the step is ten metres —
 * the smallest buffer that can be stored is 0.0062 mi or 0.01 km, both of
 * which two decimals can just about say, and in the smaller half of each pair
 * that same buffer is 10 m or 32.81 ft, nowhere near the floor. So this is the
 * floor under a smaller step rather than a case the label meets today.
 */
const SMALLEST_SHOWN_UNITS = 0.005;

const MODE_LABEL: Record<ShapefileMatchMode, string> = {
  [SHAPEFILE_MATCH_MODE.Intersecting]: "intersecting",
  [SHAPEFILE_MATCH_MODE.ContainedWithin]: "contained within"
};

/** The key every boundary predicate for this shapefile is stored under. */
export function shapefileBoundaryProperty(
  projectId: string,
  shapefileId: string
): string {
  return buildShapefileProperty(projectId, shapefileId, SHAPEFILE_BOUNDARY_COLUMN);
}

/**
 * The filter entry the toggle writes: one predicate matching every well the
 * shapefile's geometry accepts. Same shape a legend selection on the boundary
 * column produces — the value is the shapefile's name, which is what the
 * backend answers with for a matching well, and sourceKey is that same name,
 * which is what a legend selection stores — so nothing downstream has to know
 * the toggle built it. Only "contained within" writes a mode; an absent one is
 * what "intersecting" means on the wire.
 *
 * The name is trimmed here, at the one place the value is built, and an empty
 * name builds nothing: the backend trims the layer name before answering with
 * it, and predicate values match byte for byte, so a shapefile titled with
 * surrounding whitespace would otherwise filter every well away, and one
 * titled with nothing but whitespace would filter on an empty string.
 *
 * The buffer is written the same way the mode is: only when there is one, so a
 * plain filter keeps the shape it has always had on the wire and a buffer of
 * zero is the same request as no buffer at all.
 *
 * Beside it goes the way the buffer was said — the number that was committed
 * and the unit it was committed in — so the box and the chip can read back what
 * was asked for rather than the round trip through the step the filter runs on.
 * It is written only when it comes back to the stored distance, so a pair that
 * has come loose from its buffer is dropped here rather than travelling.
 */
export function buildShapefileBoundaryFilter(
  property: string,
  shapefileName: Maybe<string>,
  mode: ShapefileMatchMode,
  bufferMeters?: Maybe<number>,
  bufferDisplay?: Maybe<ShapefileBufferDisplay>
): Maybe<PropertyFilterModel> {
  const value = shapefileName?.trim();
  if (!value) {
    return null;
  }
  const predicate = withShapefileMatchMode<IPredicate>(
    {
      type: "Property",
      property,
      operator: "in",
      value: [value],
      negatedOperator: false,
      pdenSource: PdenSourceEnum.Public,
      sourceKey: value,
      groupByTitle: value
    },
    mode
  );
  const buffer = usableBufferMetres(bufferMeters);
  if (buffer > 0) {
    predicate.shapefileBufferMeters = buffer;
    const said = trustedBufferDisplay(bufferDisplay?.value, bufferDisplay?.unit, buffer);
    if (said) {
      predicate.shapefileBufferDisplayValue = said.value;
      predicate.shapefileBufferDisplayUnit = said.unit;
    }
  }
  return { predicates: [predicate], source: "legend" };
}

/**
 * The boundary predicate for one shapefile, out of every property filter the
 * user has applied. Matching is on the property alone, so a second shapefile's
 * predicate is left where it is and the two filter together.
 */
export function findShapefileBoundaryPredicate(
  propertiesFilter: Maybe<PropertyFilterModel[]>,
  property: string
): Maybe<IPredicate> {
  for (const propertyFilter of propertiesFilter ?? []) {
    const found = (propertyFilter?.predicates ?? []).find(
      (predicate) => predicate?.property === property
    );
    if (found) {
      return found;
    }
  }
  return null;
}

/**
 * Whether the predicate keeps the wells the shapefile accepts, rather than
 * dropping them. Only that kind is one of the toggle's own positions: excluding
 * a shapefile is something a legend exclude could produce, and older saved
 * filters can still carry one.
 */
export function isPlainIncludePredicate(predicate: IPredicate): boolean {
  return !predicate.negatedOperator && !SUBTRACTIVE_OPERATORS.has(predicate.operator);
}

/**
 * The predicate as it should go out: a buffer nothing reads stripped off, and
 * the one that survives healed into a distance the request can carry.
 *
 * A buffer only means something on a predicate that keeps the wells the
 * shapefile accepts: on an exclusion the toggle shows Off and the label says
 * nothing about a buffer, but the backend would still buffer what it was sent,
 * so the filter would reach further than every word about it says it does.
 *
 * A buffer that stays is rounded, capped and stepped the same way it is read,
 * so the request says what the chip and the control say. Without that a
 * predicate the buffer control never wrote — an older build's, another
 * client's, a hand-edited saved filter's — would show healed everywhere and go
 * out raw, and a fractional or out-of-range distance takes the whole filter
 * down rather than just the buffer. One that heals to nothing loses the key,
 * since a zero buffer is not the same request as no buffer.
 *
 * The way the buffer was said travels with it and is held to the same rule: it
 * goes wherever the buffer goes, and a pair that no longer comes back to the
 * distance being sent is dropped rather than carried, so nothing downstream can
 * read a wording off a buffer it does not belong to. The pair itself means
 * nothing on the wire — the backend has never heard of it and filters on the
 * metres — but it is what the chip and the box say, so it is kept honest here
 * with everything else.
 *
 * Everything else is handed back untouched, so a predicate on any other
 * property, or one already carrying a distance the request can take, is the
 * same object it came in as.
 */
export function withoutBufferOnExclusion<T extends IPredicate>(predicate: T): T {
  if (!predicate) {
    return predicate;
  }
  const buffer = predicate.shapefileBufferMeters
    ? isPlainIncludePredicate(predicate)
      ? usableBufferMetres(predicate.shapefileBufferMeters)
      : 0
    : 0;
  const said =
    buffer > 0
      ? trustedBufferDisplay(
          predicate.shapefileBufferDisplayValue,
          predicate.shapefileBufferDisplayUnit,
          buffer
        )
      : null;
  const bufferMoved = (predicate.shapefileBufferMeters ?? 0) !== buffer;
  const saidMoved =
    (predicate.shapefileBufferDisplayValue ?? null) !== (said?.value ?? null) ||
    (predicate.shapefileBufferDisplayUnit ?? null) !== (said?.unit ?? null);
  if (!bufferMoved && !saidMoved) {
    return predicate;
  }
  const next = { ...predicate };
  if (buffer > 0) {
    next.shapefileBufferMeters = buffer;
  } else {
    delete next.shapefileBufferMeters;
  }
  if (said) {
    next.shapefileBufferDisplayValue = said.value;
    next.shapefileBufferDisplayUnit = said.unit;
  } else {
    delete next.shapefileBufferDisplayValue;
    delete next.shapefileBufferDisplayUnit;
  }
  return next;
}

/**
 * The toggle position a stored predicate (or its absence) represents. Anything
 * the toggle could not have written — an exclusion, from a legend exclude or an
 * older saved filter — reads as Off, so every position stays a live choice and
 * one click replaces the odd predicate with a plain one.
 */
export function shapefileFilterModeOf(predicate: Maybe<IPredicate>): ShapefileFilterMode {
  if (!predicate || !isPlainIncludePredicate(predicate)) {
    return SHAPEFILE_FILTER_OFF;
  }
  return isContainedWithin(predicate.shapefileMatchMode)
    ? SHAPEFILE_MATCH_MODE.ContainedWithin
    : SHAPEFILE_MATCH_MODE.Intersecting;
}

/**
 * How far the stored predicate reaches outside the shapefile, in whole metres.
 * Zero for anything the buffer control could not have written, which is the
 * same answer as no buffer — the shapefile's own outline. Whatever is stored is
 * healed on the way out, so a fractional, absurd or off-step distance is read,
 * shown and written back as the one the filter actually runs at.
 */
export function shapefileBufferMetersOf(predicate: Maybe<IPredicate>): number {
  if (!predicate || !isPlainIncludePredicate(predicate)) {
    return 0;
  }
  return usableBufferMetres(predicate.shapefileBufferMeters);
}

/**
 * How the stored buffer was said when it was asked for, when the predicate
 * carries a wording that can be believed. Nothing for a predicate with no
 * buffer to say, for one whose buffer nothing shows — an exclusion — and for a
 * wording that no longer comes back to the distance stored beside it. This is
 * where the wording is checked; everywhere it is used it has already been
 * through here, so no label or box has to decide whether to trust one.
 */
export function shapefileBufferDisplayOf(
  predicate: Maybe<IPredicate>
): Maybe<ShapefileBufferDisplay> {
  const metres = shapefileBufferMetersOf(predicate);
  if (metres <= 0) {
    return null;
  }
  return trustedBufferDisplay(
    predicate?.shapefileBufferDisplayValue,
    predicate?.shapefileBufferDisplayUnit,
    metres
  );
}

/**
 * The staged shapefile filter configuration: what the band shows while it
 * differs from the applied filter, and what a press of the Filter button writes.
 * `bufferMetres` is already normalized — capped, quantized, `0` for none —
 * exactly as the applied reading is, so the two can be compared field for
 * field.
 */
export interface ShapefileFilterDraft {
  mode: ShapefileFilterMode;
  bufferMetres: number;
  bufferDisplay: Maybe<ShapefileBufferDisplay>;
}

/**
 * Whether two buffers are the same distance, treating an absent buffer and a
 * zero buffer as one value: "no buffer". Never a truthiness test — a buffer
 * of `0` is a legitimate value, not a falsy one to be confused with `null` or
 * `undefined`.
 */
export function sameShapefileBufferMetres(a: Maybe<number>, b: Maybe<number>): boolean {
  const normalized = (value: Maybe<number>) => (value == null ? 0 : value);
  return normalized(a) === normalized(b);
}

/**
 * Whether there is anything for the Filter button to write: the staged mode
 * differs from the applied one, or the staged buffer does once absent and
 * zero are treated as the same thing. The buffer's display pair is never an
 * independent input here — it cannot change without the metres changing,
 * except into the same ten-metre bucket, which is exactly the case that
 * should stay clean (D-02).
 */
export function isShapefileDraftDirty(
  draft: ShapefileFilterDraft,
  appliedMode: ShapefileFilterMode,
  appliedBufferMetres: Maybe<number>
): boolean {
  return (
    draft.mode !== appliedMode ||
    !sameShapefileBufferMetres(draft.bufferMetres, appliedBufferMetres)
  );
}

/**
 * Whether the Filter button may be pressed: there is something to apply, and the
 * shapefile has a usable name to match on. A name of nothing but whitespace
 * trims away to nothing, so it is refused here rather than filtering on it.
 */
export function canApplyShapefileDraft(
  shapefileName: Maybe<string>,
  draft: ShapefileFilterDraft,
  appliedMode: ShapefileFilterMode,
  appliedBufferMetres: Maybe<number>
): boolean {
  return (
    isShapefileDraftDirty(draft, appliedMode, appliedBufferMetres) &&
    !!shapefileName?.trim()
  );
}

/**
 * The buffer part of the label, in the unit it is asked for. The distance is
 * one number stored in metres, so the same predicate reads "(+5 km)" on a
 * metric screen, "(+3.11 mi)" on an imperial one, and "(+5000 m)" for whoever
 * has the buffer switch set to metres — the same buffer, said several ways,
 * the way every other length on the screen is.
 *
 * Miles unless told otherwise, so a caller that has no reader to ask keeps the
 * text it has always had. Two decimals with the trailing zeros dropped: 1610 m
 * reads "+1 mi", "+1.61 km" or "+1610 m". The distance spelled here is the
 * healed one, so the chip names the distance the filter ran at rather than
 * whatever was stored. A buffer too small to show in two decimals of the unit
 * being used reads as less than the smallest one rather than as "+0", which
 * would say there is no buffer while one is applied.
 *
 * The one exception is the unit the buffer was asked for in. A distance said in
 * a unit is rarely a whole number of ten-metre steps, so converting back would
 * hand a reader a number they did not type: a thousand feet stored as the 300 m
 * the filter measures at reads 984.25 ft. When the wording that came with the
 * buffer is in the unit being read in, that number is said back word for word
 * instead. A reader in any other unit converts, so one filter still reads
 * "(+1000 ft)" to whoever typed it and "(+0.19 mi)" to whoever is in miles.
 *
 * The wording is handed in already checked — nothing here decides whether to
 * believe it — so this stays a plain function of its arguments.
 */
function bufferSuffix(
  meters: number,
  said: Maybe<ShapefileBufferDisplay>,
  unit: ShapefileBufferLabelUnit
): string {
  if (meters <= 0) {
    return "";
  }
  if (said && said.unit === unit) {
    return ` (+${said.value} ${unit})`;
  }
  const distance = meters / METRES_PER_LABEL_UNIT[unit];
  if (distance < SMALLEST_SHOWN_UNITS) {
    return ` (+<0.01 ${unit})`;
  }
  return ` (+${Number(distance.toFixed(2))} ${unit})`;
}

/**
 * Chip and filter-tree text for a boundary predicate: the shapefile's name,
 * how it is being matched, and how far past it the match reaches. The reserved
 * column name behind the property never appears, and the mode is spelled out
 * both ways so the chip says the same thing the toggle does. An exclusion reads
 * the way every other excluding predicate does and carries neither a mode nor a
 * buffer, since the toggle is showing Off for it.
 *
 * The buffer is spelled in the unit the caller hands in, which is the one the
 * reader has the buffer switch set to — this stays a plain function of what it
 * is given, so the chip, the tree and every test say the same thing for the
 * same pair of arguments.
 */
export function shapefileBoundaryPredicateLabel(
  predicate: IPredicate,
  bufferUnit: ShapefileBufferLabelUnit = UNIT_IDS.MILES
): string {
  const name =
    predicate?.groupByTitle ||
    (Array.isArray(predicate?.value) ? predicate.value[0] : predicate?.value) ||
    "";
  if (predicate && !isPlainIncludePredicate(predicate)) {
    return `${PREDICATE_PROPERTY_OPERATOR.NotEqual} ${name}`;
  }
  const mode = isContainedWithin(predicate?.shapefileMatchMode)
    ? SHAPEFILE_MATCH_MODE.ContainedWithin
    : SHAPEFILE_MATCH_MODE.Intersecting;
  return `${name}${MODE_SEPARATOR}${MODE_LABEL[mode]}${bufferSuffix(
    shapefileBufferMetersOf(predicate),
    shapefileBufferDisplayOf(predicate),
    bufferUnit
  )}`;
}
